import React, { useState } from 'react';
import { PlusIcon } from 'lucide-react';
import { usePortal } from '../../contexts/PortalContext';
import type { NewDeliverableInput } from '../../contexts/PortalContext';
import { DELIVERABLE_STATUSES, DELIVERABLE_TYPES, VIDEO_FORMATS } from '../../types/portal';
import { isVersioned, linkFieldsFor, statusOptionLabel } from '../../utils/deliverables';
import { BUTTON_GHOST, BUTTON_QUIET, BUTTON_SOLID, FIELD, OPTION, SELECT } from './adminStyles';
import { TranscriptUpload } from './TranscriptUpload';

/**
 * New work always begins planned, with no version until it reaches review —
 * and **shown to the client**.
 *
 * **It used to default to hidden (changed 17 Oct 2026).** The setup flow
 * writes every piece it creates with `visible_to_client: true`, so the same
 * object had opposite defaults depending on which door you came through:
 * the six episodes created at setup were live on the client's page, and
 * episode seven, added here a month later, silently was not. Tom found it
 * asking whether adding a deliverable reaches the client page. It did not.
 *
 * Visible is the right default because it matches what a producer means. A
 * planned piece shows the client the slate of what is coming — a tile
 * reading *Editing, due 16 November* — which is the whole reason the setup
 * flow puts them up in the first place. Hiding one is the exception, and
 * the switch is right there for it.
 */
const EMPTY: NewDeliverableInput = {
  name: '',
  type: 'Video',
  status: 'Planned',
  version: '',
  driveUrl: '',
  audioUrl: '',
  visibleToClient: true,
  videoFormat: '16:9 Landscape',
  transcript: ''
};
export function AddDeliverable() {
  const {
    addDeliverable,
    notify
  } = usePortal();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState<NewDeliverableInput>(EMPTY);
  const links = linkFieldsFor(input.type);
  const versioned = isVersioned(input.status);
  function edit(patch: Partial<NewDeliverableInput>) {
    setInput(current => ({
      ...current,
      ...patch
    }));
  }
  function submit(event: React.FormEvent) {
    event.preventDefault();
    const name = input.name.trim();
    if (!name) return;
    /** Versions only exist once the status reaches Review or Final Delivery. */
    addDeliverable({
      ...input,
      name,
      version: versioned ? input.version : ''
    });
    notify(input.visibleToClient ? `${name} added · on their page` : `${name} added · hidden`);
    setInput(EMPTY);
    setOpen(false);
  }
  if (!open) {
    return <button type="button" onClick={() => setOpen(true)} className={`${BUTTON_GHOST} mt-8 inline-flex items-center gap-3`} data-id="element-451" data-mp="16t9cz5950s7z">
        <PlusIcon className="h-3.5 w-3.5" strokeWidth={1.5} data-id="element-452" data-mp="1btdsaryb7gwm" />
        Add Deliverable
      </button>;
  }
  return <form onSubmit={submit} className="mt-8 border border-paper/25 px-6 py-8 sm:px-8" data-id="element-453" data-mp="1frv25j9475hq">
      <input value={input.name} onChange={event => edit({
      name: event.target.value
    })} placeholder="e.g. Speaker Interview 01" aria-label="Deliverable title" autoFocus className="w-full border-0 border-b border-paper/25 bg-transparent pb-3 font-display text-3xl leading-none text-paper placeholder:text-paper/30 focus:border-paper focus:outline-none sm:text-4xl" data-id="element-454" data-mp="3dm22xmda96mf" />

      <div className="mt-8 grid grid-cols-1 gap-7 sm:grid-cols-2" data-id="element-455" data-mp="2wamxo8bem6cw">
        <label className="block" data-id="element-456" data-mp="2wchm3wlvwglq">
          <span className="label block text-paper/40" data-id="element-457" data-mp="3t0nn89jlkqv9">Type</span>
          <select value={input.type} onChange={event => edit({
          type: event.target.value as NewDeliverableInput['type']
        })} className={SELECT} data-id="element-458" data-mp="3t04cpdwa14r9">
            {DELIVERABLE_TYPES.map(option => <option key={option} value={option} className={OPTION} data-id="element-459" data-mp="3f1nyz2zzydzm">
                {option}
              </option>)}
          </select>
        </label>

        <label className="block" data-id="element-460" data-mp="28ippcr75xwgj">
          <span className="label block text-paper/40" data-id="element-461" data-mp="23lg31kf2m0wx">Status</span>
          <select value={input.status} onChange={event => edit({
          status: event.target.value as NewDeliverableInput['status']
        })} className={SELECT} data-id="element-462" data-mp="23obzt5f6mjjs">
            {DELIVERABLE_STATUSES.map(option => <option key={option} value={option} className={OPTION} data-id="element-463" data-mp="3ijttr55h96ip">
                {statusOptionLabel(option)}
              </option>)}
          </select>
        </label>

        {input.type === 'Video' && <label className="block" data-id="element-464" data-mp="j2vjg2e6x54y">
            <span className="label block text-paper/40" data-id="element-465" data-mp="j33urpuo02e1">Video Format</span>
            <select value={input.videoFormat ?? '16:9 Landscape'} onChange={event => edit({
          videoFormat: event.target.value as NewDeliverableInput['videoFormat']
        })} className={SELECT} data-id="element-466" data-mp="53cmm5qtxg7y">
              {VIDEO_FORMATS.map(option => <option key={option} value={option} className={OPTION} data-id="element-467" data-mp="2utfzr93c0xlh">
                  {option}
                </option>)}
            </select>
          </label>}

        {versioned && <label className="block" data-id="element-468" data-mp="2i90x9gx979md">
            <span className="label block text-paper/40" data-id="element-469" data-mp="2ic56wtthmxkf">Version</span>
            <input value={input.version} onChange={event => edit({
          version: event.target.value
        })} placeholder="e.g. V1" className={FIELD} data-id="element-470" data-mp="2w91wkrxve8zq" />
          </label>}
      </div>

      {input.type === 'Transcript' && <TranscriptUpload value={input.transcript ?? ''} filename={input.transcriptFilename} wordCount={input.transcriptWordCount} pending
    /* The deliverable does not exist yet, so the text is held only until
       Add Deliverable creates the row it belongs to — at which point it
       is stored immediately, and any refusal is reported. */ onUpload={async (text, filename, words) => {
      edit({
        transcript: text,
        transcriptFilename: filename,
        transcriptWordCount: words
      });
      return {
        ok: true
      };
    }} data-id="element-471" data-mp="1yn4hz5mm2nt2" />}

      {links.drive && <label className="mt-8 block" data-id="element-472" data-mp="3pncy86diumf3">
          <span className="label block text-paper/40" data-id="element-473" data-mp="3pofpwgio4sw2">{links.driveLabel}</span>
          <input value={input.driveUrl} onChange={event => edit({
        driveUrl: event.target.value
      })} placeholder="Paste Drive file or folder link" className={FIELD} data-id="element-474" data-mp="3tphocv4rh0wz" />
        </label>}

      {links.audio && <label className="mt-8 block" data-id="element-475" data-mp="31zmy8w92x779">
          <span className="label block text-paper/40" data-id="element-476" data-mp="35ycaummamkpu">Audio Review Link</span>
          <input value={input.audioUrl} onChange={event => edit({
        audioUrl: event.target.value
      })} placeholder="Paste Drive audio link" className={FIELD} data-id="element-477" data-mp="3ay8cwgbfk22r" />
        </label>}

      <label className="mt-8 flex items-center justify-between gap-6 border-b border-paper/25 pb-3" data-id="element-478" data-mp="q6yaixmtxxlp">
        <span className="label text-paper/40" data-id="element-479" data-mp="10boe17xvt9s1">Shown in Client Portal</span>
        <button type="button" onClick={() => edit({
        visibleToClient: !input.visibleToClient
      })} aria-pressed={input.visibleToClient} className={['label border px-3 py-2 transition-colors duration-150 ease-out', input.visibleToClient ? 'border-paper bg-paper text-ink' : 'border-paper/30 text-paper/40 hover:border-paper/60'].join(' ')} data-id="element-480" data-mp="vctnfjtoagxd">
          {input.visibleToClient ? 'On' : 'Off'}
        </button>
      </label>

      <div className="mt-10 flex flex-wrap items-center gap-6" data-id="element-481" data-mp="7nsja8rvhrxw">
        <button type="submit" className={BUTTON_SOLID} data-id="element-482" data-mp="3mp65u17ma0d">
          Add Deliverable
        </button>
        <button type="button" onClick={() => {
        setInput(EMPTY);
        setOpen(false);
      }} className={BUTTON_QUIET} data-id="element-483" data-mp="221thqsjigcxg">
          Cancel
        </button>
      </div>
    </form>;
}